import { type ReactNode, useEffect, useRef, useState } from 'react'
import { cn } from '~/lib/utils'

type ScrollRevealProps = {
  children: ReactNode
  className?: string
  /** Stagger delay in milliseconds. */
  delay?: number
  as?: 'div' | 'section' | 'li' | 'span'
}

/*
 * Reveals its children with a soft fade-and-rise the first time they enter
 * the viewport, mirrors the scroll choreography on the marketing site.
 *
 * Prerender-safe: the SSR/SSG HTML is rendered as `static` (already visible,
 * no transition), so the page is fully readable for Googlebot's no-JS pass,
 * slow networks, and JS-disabled clients. Once JS hydrates, content that
 * happens to be *above the fold* stays static (no jarring fade-in of the LCP
 * block); content *below the fold* is hidden instantly and armed for an
 * IntersectionObserver-driven fade as the user scrolls down.
 */
export const ScrollReveal = ({
  children,
  className,
  delay = 0,
  as: Tag = 'div',
}: ScrollRevealProps) => {
  const ref = useRef<HTMLElement>(null)
  const [phase, setPhase] = useState<'static' | 'hidden' | 'revealed'>('static')

  useEffect(() => {
    const node = ref.current
    // biome-ignore lint/suspicious/noUnnecessaryConditions: React assigns ref.current on mount; Biome misses ref mutations
    if (!node) {
      return
    }

    const rect = node.getBoundingClientRect()
    const alreadyInView = rect.top < window.innerHeight * 0.92
    if (alreadyInView) {
      return
    }

    setPhase('hidden')

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setPhase('revealed')
            observer.unobserve(node)
          }
        }
      },
      { rootMargin: '0px 0px -5% 0px', threshold: 0 }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const animated = phase !== 'static'

  return (
    <Tag
      className={cn(
        animated &&
          'transition-[opacity,transform] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] [will-change:opacity,transform] motion-reduce:transition-none',
        phase === 'hidden'
          ? 'translate-y-7 opacity-0 motion-reduce:translate-y-0 motion-reduce:opacity-100'
          : 'translate-y-0 opacity-100',
        className
      )}
      ref={ref as never}
      style={delay && animated ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </Tag>
  )
}
